<template>
    <div class="download-pdf">
        <modal
            v-if="!!showPDF"
            :value="!!showPDF"
            :touch-to-close="false"
            @close="close"
        >
            <div
                v-if="showConfig"
                id="pdf-config"
                ref="pdfConfig"
                :class="{
                    disable: status.working
                }"
            >
                <div class="title">
                    {{ model.title }}
                </div>
                <div class="sub-title">
                    {{ model.localName }}
                    <span
                        v-if="!printAll"
                        class="reset-button material-icons"
                        @click="resetPrintAll"
                    >star_outline
                    </span>
                    <span
                        v-else
                        class="material-icons"
                    >star
                    </span>
                </div>
                <div class="select-all">
                    <span
                        v-if="!printAll"
                        class="reset-button"
                        @click="resetPrintAll"
                    >
                        <span class="material-icons">star_outline</span>
                        <span>Select All Sections</span>
                    </span>
                    <span
                        v-else
                    >
                        <span class="material-icons">star</span>
                        <span>All Sections Selected</span>
                    </span>
                </div>
                <div class="config-checkbox-container">
                    <checkbox-tree
                        :sections="sections"
                        :status="status"
                    />
                </div>
                <progress-indicator
                    v-if="status.working || forceProgress"
                    :percent="Math.round(percent * 100) "
                />
                <div
                    v-if="status.working"
                    class="button loading-button"
                >
                    {{ status.message }}
                </div>
                <div
                    v-else
                    class="button save-button clickable"
                    @click.stop="savePDF"
                >
                    Save
                </div>
            </div>
            <!-- DOM template, if necessary, for debugging or referencing templated dom elements -->
            <!-- TODO: When we apply CSS, we may need to enable v-show, but hide this offscreen instead -->
            <!-- This container could be a template for an offscreeen DOM, if needed? -->
            <!-- Tools like html2canvas require elements in DOM, so may be easeir to have this template instead of all in JS -->

            <!-- Could get populated with thumbnail canvases of the PDF pages, as they are rendered? -->
            <div
                v-else
                class="download-options"
                :class="{ working : status.working }"
            >
                <div class="options-title">
                    {{ status.working ? 'Creating Report' : 'Download PDF' }}
                </div>
                <progress-indicator
                    v-if="status.working || forceProgress"
                    :percent="Math.round(percent * 100)"
                />
                <div
                    v-else
                    class="options-container"
                >
                    <div
                        class="report-button full-report-button"
                        @click="savePDF"
                    >
                        Full Report
                    </div>
                    <div
                        class="report-button custom-report-button"
                        @click="createCustomReport"
                    >
                        Create Custom Report
                    </div>
                    <div
                        class="report-button download-faq-button"
                        @click="saveFile('FAQ_for_Community_Profiles.pdf')"
                    >
                        Download FAQ
                    </div>
                    <div
                        class="report-button chart-guide-button"
                        @click="saveFile('Chart_Guide_for_Community_Profiles.pdf')"
                    >
                        Chart Guide
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import { saveAs } from 'file-saver';
import { createPDFCreator } from '@/services/print/PDFCreator';
import PDFModel from '@/model/PDFModel';
import PDFDocument from '@/views/print/PDFDocument';
import Modal from '@/views/Modal.vue';
import CheckboxTree from '@/components/CheckboxTree.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import { isIE } from '@/services/helpers';

export default {
    components: {
        ProgressIndicator,
        Modal,
        CheckboxTree,
    },
    props: {
        model: {
            type: PDFModel,
            required: true
        }
    },
    data() {
        return {
            showConfig: false,
            forceProgress: false,
        };
    },
    computed: {
        pdfCreator() {
            return createPDFCreator(new PDFDocument(this.model));
        },
        status() {
            return this.pdfCreator.status;
        },
        percent() {
            const status = this.pdfCreator.status;
            return (status.created + status.loaded + status.rendered) / (status.total * 3);
        },
        showPDF() {
            return this.model.showPDF;
        },
        sections() {
            return this.pdfCreator.sections;
        },
        fileName() {
            return this.model.fileName;
        },
        printAll() {
            return this.pdfCreator.printAll;
        }
    },
    watch: {
        showPDF(show) {
            if (show) {
                this.pdfCreator.init();
                this.model.parent.mobileNavModel.selected = null;
                document.body.style.overflowY = 'hidden';
            }
        }
    },
    methods: {
        resetPrintAll() {
            this.pdfCreator.printAll = true;
        },
        createCustomReport(e) {
            this.showConfig = true;
            // By default, resets the printall.
            // Hold down alt or shift to use last selected
            if (!e.altKey && !e.shiftKey) {
                this.resetPrintAll();
            }
        },
        async savePDF() {
            try {
                if (!this.showConfig) {
                    this.resetPrintAll();
                }

                if (isIE() || this.$screens.mobile) {
                    this.forceProgress = true;
                    await new Promise(resolve => setTimeout(resolve, 250));
                }

                await this.pdfCreator.create(!this.showConfig);
                await this.pdfCreator.save(this.fileName);
                this.close();
            } catch (e) {
                console.error(e);
                this.close();
            } finally {
                this.forceProgress = false;
            }
        },
        saveFile(filename) {
            saveAs(`${process.env.BASE_URL}data/indicators/help/${filename}`, filename);
        },
        close() {
            document.body.style.overflowY = 'scroll';
            this.showConfig = false;
            this.model.showPDF = false;
        },
    }
};
</script>

<style lang="postcss" scoped>
#pdf-config {
    @apply px-8 mt-8;
    margin-bottom: 150px;
}

#pdf-config.disable {
    @apply cursor-wait;
}

.title {
    @apply text-3xl text-print-7;
}

.sub-title {
    @apply text-2xl text-print-1;
}
.select-all {
    @apply mb-2;
    line-height: 1;
}

.sub-title span.material-icons {
    @apply text-xs mb-4;
    vertical-align: middle;
}

.select-all span {
    @apply text-xs text-print-1;
    vertical-align: middle;
    line-height: 1;
}

.reset-button {
    @apply cursor-pointer;
}

.config-checkbox-container {
    width: 400px;
}

.button {
    @apply inline-flex items-center justify-center text-lg text-center cursor-pointer border-2 rounded-lg py-1 px-2 my-4;
    min-width: 120px;
}

.loading-button {
    @apply opacity-50;
}

.button.clickable:hover {
    @apply bg-neutral-5;
}

.download-options {
    @apply inline-flex relative flex-col p-4 pointer-events-none;
    z-index: 11;
}

.options-title {
    @apply text-3xl font-bold text-primary-1 pt-2 mb-3;
}

.options-container {
    @apply flex flex-row;
    flex-wrap: wrap;
}

.report-button {
    @apply inline-flex relative text-xs py-1 uppercase justify-center items-center cursor-pointer font-bold text-neutral-text m-3;
    pointer-events: all;
    white-space: nowrap;
    height: 36px;
}

.full-report-button {
    @apply text-white bg-secondary-teal px-6 mr-3 ml-0;
    border-radius: 18px;
}

.chart-guide-button {
    @apply mr-0 ml-3;
}

@screen mobile {
    .config-checkbox-container {
        width: 100%;
    }
}
</style>
